import { SalesOrderPaymentStatus } from '@/lib/enums/SalesOrderPaymentStatus'
import { SalesOrderFulfillmentStatus } from '@/lib/enums/SalesOrderFulfillmentStatus'
import { SalesOrderRefundStatus } from '@/lib/enums/SalesOrderRefundStatus'
import { DeliveryServiceabilityStatus } from '@/lib/enums/DeliveryServiceabilityStatus'
import { FulfillmentType } from '@/lib/enums/FulfillmentType'
import type {
  SalesOrderCollectionClosedReason,
  SalesOrderCollectionStatus,
} from '@/lib/services/sales-order-payments/sales-order-payments.types'
import type {
  SalesOrderFinancialState,
  SalesOrderListItem,
} from '@/lib/services/sales-orders/sales-orders.types'
import { normalizeCurrencyValue, parseCurrency, readProperty } from '@/lib/utils'
import { getFulfillmentDuePresentation } from './fulfillment-date-presentation'

/** The order fields a helper reads: a full order and an index row both carry them. */
type SalesOrderFields<K extends keyof SalesOrderListItem> = Pick<SalesOrderListItem, K>

type FinancialStateTotalField =
  | 'collected_total'
  | 'refunded_total'
  | 'retained_total'
  | 'balance_due_total'
  | 'settlement_remaining_total'

type OrderSourceLinkPermission = 'can_cancel' | 'can_refund' | 'can_collect_payment'

export interface FsnNetworkFulfillmentPresentation {
  label: string
  variant: 'muted' | 'success' | 'warning' | 'destructive'
}

const FSN_NETWORK_FULFILLMENT_TYPE = 'fsn_network'
const FSN_DELIVERY_CONFIRMATION_DELIVERED = 'delivered'
const NON_ACTIVE_FSN_NETWORK_STATUSES = new Set(['Problem', 'Cancelled'])

/** Whether FSN named the shop filling the order; `filler` is the FSN order's raw filler metadata. */
export function hasFsnFiller(filler: unknown): boolean {
  const fillerName = readProperty(filler, 'name')

  return typeof fillerName === 'string' && fillerName.trim() !== ''
}

function readFinancialStateTotal(
  order: SalesOrderFields<'financial_state'>,
  financialStateFieldName: FinancialStateTotalField
): number {
  const value = order?.financial_state?.[financialStateFieldName]

  if (value != null) {
    return normalizeCurrencyValue(parseCurrency(value))
  }

  return 0
}

function readOrderSourceLinkBooleanField(
  order: SalesOrderFields<'order_source_link'>,
  sourceLinkFieldName: OrderSourceLinkPermission
): boolean | null {
  const sourceLink = order?.order_source_link

  if (typeof sourceLink?.[sourceLinkFieldName] === 'boolean') {
    return sourceLink[sourceLinkFieldName]
  }

  return null
}

export function isIncomingFsnNetworkSalesOrder(
  order: SalesOrderFields<'order_source_link'>
): boolean {
  const sourceLink = order?.order_source_link

  return (
    sourceLink?.source_system === 'fsn_network' &&
    readProperty(sourceLink.metadata, 'direction') === 'incoming'
  )
}

export function isCondolencesSalesOrder(order: SalesOrderFields<'order_source_link'>): boolean {
  return order?.order_source_link?.source_system === 'condolences'
}

export function isExternalFinancialSalesOrder(
  order: SalesOrderFields<'order_source_link'>
): boolean {
  const sourceLink = order?.order_source_link

  if (!sourceLink) {
    return false
  }

  if (sourceLink.financial_owner) {
    return sourceLink.financial_owner !== 'tukios_flowers'
  }

  if (
    sourceLink.source_system === 'fsn_network' &&
    readProperty(sourceLink.metadata, 'direction') === 'incoming'
  ) {
    return true
  }

  return sourceLink.can_collect_payment === false
}

export function getExternalFinancialProviderLabel(
  order: SalesOrderFields<'order_source_link'>
): string {
  const sourceLink = order?.order_source_link
  const providerLabel = readProperty(sourceLink?.metadata, 'provider_label')

  if (typeof providerLabel === 'string' && providerLabel) {
    return providerLabel
  }

  if (sourceLink?.source_system === 'condolences') {
    return 'Condolences'
  }

  if (sourceLink?.source_system === 'fsn_network') {
    return 'Flower Shop Network'
  }

  return 'External source'
}

function readFinancialStateCollectionStatus(
  order: SalesOrderFields<'financial_state'>
): SalesOrderFinancialState['collection_status'] | null {
  const collectionStatus = order?.financial_state?.collection_status

  return typeof collectionStatus === 'string' && collectionStatus.length > 0
    ? collectionStatus
    : null
}

function readFinancialStateCollectionClosedReason(
  order: SalesOrderFields<'financial_state'>
): SalesOrderCollectionClosedReason | null {
  const closedReason = order?.financial_state?.collection_closed_reason

  return typeof closedReason === 'string' && closedReason.length > 0 ? closedReason : null
}

export function hasSalesOrderFinancialState(order: SalesOrderFields<'financial_state'>): boolean {
  return order?.financial_state != null
}

export function isCancelledSalesOrder(order: SalesOrderFields<'cancelled_at'>): boolean {
  return Boolean(order?.cancelled_at)
}

export function isRefundedSalesOrder(order: SalesOrderFields<'refund_status'>): boolean {
  return order?.refund_status === SalesOrderRefundStatus.Refunded.Value
}

export function isInStoreSalesOrder(order: SalesOrderFields<'fulfillment_type'>): boolean {
  return order?.fulfillment_type === FulfillmentType.InStore.Value
}

export function isFulfilledSalesOrder(order: SalesOrderFields<'fulfillment_status'>): boolean {
  return order?.fulfillment_status === SalesOrderFulfillmentStatus.Fulfilled.Value
}

export function isDeliveredCondolencesSalesOrder(
  order: SalesOrderFields<'order_source_link' | 'fulfillment_status'>
): boolean {
  return isCondolencesSalesOrder(order) && isFulfilledSalesOrder(order)
}

function isFsnDeliveryConfirmed(fsn: unknown): boolean {
  const deliveryConfirmationStatus = readProperty(
    readProperty(fsn, 'delivery_confirmation'),
    'status'
  )

  return (
    typeof deliveryConfirmationStatus === 'string' &&
    deliveryConfirmationStatus.toLowerCase() === FSN_DELIVERY_CONFIRMATION_DELIVERED
  )
}

export function isActiveFsnNetworkFulfillment(
  order: SalesOrderFields<'order_fulfillment'>
): boolean {
  const fulfillment = order?.order_fulfillment

  if (fulfillment?.type !== FSN_NETWORK_FULFILLMENT_TYPE || !fulfillment?.external_fulfillment_id) {
    return false
  }

  const fsn = readProperty(fulfillment.metadata, 'fsn')
  const status = readProperty(fsn, 'status')

  if (status === 'Complete') {
    return !isFsnDeliveryConfirmed(fsn)
  }

  return !(typeof status === 'string' && NON_ACTIVE_FSN_NETWORK_STATUSES.has(status))
}

export function getFsnNetworkFulfillmentPresentation(
  order: SalesOrderFields<'order_fulfillment'>
): FsnNetworkFulfillmentPresentation | null {
  const fulfillment = order?.order_fulfillment

  if (fulfillment?.type !== FSN_NETWORK_FULFILLMENT_TYPE || !fulfillment?.external_fulfillment_id) {
    return null
  }

  const fsn = readProperty(fulfillment.metadata, 'fsn')

  switch (readProperty(fsn, 'status')) {
    case 'Complete':
      if (!isFsnDeliveryConfirmed(fsn)) {
        return { label: 'Accepted', variant: 'muted' }
      }

      return { label: 'Fulfilled by FSN', variant: 'success' }
    case 'Accepted':
      return { label: 'Accepted', variant: 'muted' }
    case 'Problem':
      return { label: 'FSN Problem', variant: 'warning' }
    case 'Cancelled':
      return { label: 'FSN Cancelled', variant: 'destructive' }
    default:
      return { label: 'Sent to FSN', variant: 'muted' }
  }
}

export function needsFsnNetworkFulfillment(
  order: SalesOrderFields<
    | 'order_fulfillment'
    | 'fulfillment_type'
    | 'delivery_serviceability_status'
    | 'fulfillment_status'
    | 'cancelled_at'
    | 'refund_status'
  >
): boolean {
  const fulfillment = order?.order_fulfillment
  const hasFsnOrderId =
    fulfillment?.type === FSN_NETWORK_FULFILLMENT_TYPE && fulfillment?.external_fulfillment_id

  return (
    order?.fulfillment_type === FulfillmentType.Delivery.Value &&
    order?.delivery_serviceability_status ===
      DeliveryServiceabilityStatus.OutsideDeliveryArea.Value &&
    order?.fulfillment_status !== SalesOrderFulfillmentStatus.Fulfilled.Value &&
    !isCancelledSalesOrder(order) &&
    !isRefundedSalesOrder(order) &&
    !hasFsnOrderId
  )
}

export function usesCancelledPaymentPresentation(
  order: SalesOrderFields<'cancelled_at' | 'refund_status'>
): boolean {
  return isCancelledSalesOrder(order) && !isRefundedSalesOrder(order)
}

export function usesRefundPaymentPresentation(order: SalesOrderFields<'refund_status'>): boolean {
  return (
    order?.refund_status === SalesOrderRefundStatus.PartialRefund.Value ||
    order?.refund_status === SalesOrderRefundStatus.Refunded.Value
  )
}

export function shouldShowSalesOrderHeaderFulfillmentStatus(
  order: SalesOrderFields<'cancelled_at' | 'refund_status' | 'fulfillment_status'>
): boolean {
  // A cancelled order says Cancelled once in the header: in the payment badge, or here when a refund badge
  // takes that place.
  if (isCancelledSalesOrder(order)) {
    return usesRefundPaymentPresentation(order)
  }

  return !(
    order?.refund_status === SalesOrderRefundStatus.Refunded.Value &&
    order?.fulfillment_status === SalesOrderFulfillmentStatus.ToDo.Value
  )
}

export function getSalesOrderGrossPaidTotal(order: SalesOrderFields<'financial_state'>): number {
  return readFinancialStateTotal(order, 'collected_total')
}

export function getSalesOrderGrossRefundedTotal(
  order: SalesOrderFields<'financial_state'>
): number {
  return readFinancialStateTotal(order, 'refunded_total')
}

export function getSalesOrderNetCollectedTotal(order: SalesOrderFields<'financial_state'>): number {
  return readFinancialStateTotal(order, 'retained_total')
}

export function getSalesOrderCollectedActivityTotal(
  order: SalesOrderFields<'financial_state'>
): number {
  return getSalesOrderGrossPaidTotal(order)
}

export function getSalesOrderRemainingTotal(order: SalesOrderFields<'financial_state'>): number {
  return readFinancialStateTotal(order, 'balance_due_total')
}

export function getSalesOrderCollectionClosedReason(
  order: SalesOrderFields<'financial_state'>
): SalesOrderCollectionClosedReason | null {
  return readFinancialStateCollectionClosedReason(order)
}

export function getSalesOrderCollectionStatus(
  order: SalesOrderFields<'financial_state'>
): SalesOrderCollectionStatus | null {
  return readFinancialStateCollectionStatus(order)
}

// Route stops ask this too: their orders may be missing or may not carry the payment-policy fields.
export function currentCustomerAllowsFulfillmentBeforePayment(
  order:
    | Partial<
        SalesOrderFields<
          'current_customer_allows_fulfillment_before_payment' | 'allow_fulfillment_before_payment'
        >
      >
    | null
    | undefined
): boolean {
  return (
    (order?.current_customer_allows_fulfillment_before_payment ??
      order?.allow_fulfillment_before_payment) === true
  )
}

type FulfillmentUpdateFields = SalesOrderFields<
  | 'cancelled_at'
  | 'order_fulfillment'
  | 'order_source_link'
  | 'fulfillment_status'
  | 'refund_status'
  | 'payment_status'
  | 'current_customer_allows_fulfillment_before_payment'
  | 'allow_fulfillment_before_payment'
>

export function canUpdateSalesOrderFulfillment(order: FulfillmentUpdateFields): boolean {
  return (
    !isCancelledSalesOrder(order) &&
    !isActiveFsnNetworkFulfillment(order) &&
    !isDeliveredCondolencesSalesOrder(order) &&
    order?.refund_status !== SalesOrderRefundStatus.Refunded.Value &&
    (order?.payment_status === SalesOrderPaymentStatus.Paid.Value ||
      currentCustomerAllowsFulfillmentBeforePayment(order))
  )
}

export function getSalesOrderFulfillmentDuePresentation(
  order: FulfillmentUpdateFields & SalesOrderFields<'fulfillment_due_date'>
): ReturnType<typeof getFulfillmentDuePresentation> {
  return getFulfillmentDuePresentation({
    cancelledAt: order?.cancelled_at,
    refundStatus: order?.refund_status,
    fulfillmentDueDate: order?.fulfillment_due_date,
    fulfillmentStatus: order?.fulfillment_status,
    canShowAttention: canUpdateSalesOrderFulfillment(order),
  })
}

export function canCancelSalesOrder(
  order: SalesOrderFields<
    'order_source_link' | 'cancelled_at' | 'fulfillment_status' | 'fulfillment_type'
  >
): boolean {
  if (readOrderSourceLinkBooleanField(order, 'can_cancel') === false) {
    return false
  }

  return (
    !isCancelledSalesOrder(order) && (!isFulfilledSalesOrder(order) || isInStoreSalesOrder(order))
  )
}

type CollectPaymentFields = SalesOrderFields<
  'financial_state' | 'order_source_link' | 'cancelled_at' | 'refund_status' | 'payment_status'
>

export function canCollectSalesOrderPayment(order: CollectPaymentFields): boolean {
  if (readOrderSourceLinkBooleanField(order, 'can_collect_payment') === false) {
    return false
  }

  return order?.financial_state?.can_collect_payment === true
}

export function canCreateSalesOrderRefund(
  order: SalesOrderFields<
    'financial_state' | 'order_source_link' | 'cancelled_at' | 'refund_status'
  >
): boolean {
  if (readOrderSourceLinkBooleanField(order, 'can_refund') === false) {
    return false
  }

  // Money goes back only while the order holds some (`financial_state.refund_mode`).
  return (
    !isCancelledSalesOrder(order) &&
    !isRefundedSalesOrder(order) &&
    order?.financial_state?.refund_mode !== undefined &&
    order.financial_state.refund_mode !== 'none'
  )
}

export function shouldShowSalesOrderBalanceDue(order: CollectPaymentFields): boolean {
  return canCollectSalesOrderPayment(order)
}
